/**
 * The pure rules of Docs › Team setup (ADR-217 with Amendments 1 and 2;
 * specs/changes/team-setup/contract.md, "The console").
 */
import type { ValueAnswerListed } from '@/services/teamSetupApi';

export const TEAM_SETUP_PATH = '/docs/team-setup';

/** The four tabs, each with its own address (`/docs/team-setup/<key>`). */
export const TEAM_SETUP_TABS = [
  { key: 'skills', title: 'Skills' },
  { key: 'hooks', title: 'Hooks & MCP' },
  { key: 'instructions', title: 'Instructions' },
  { key: 'memory', title: 'Memory' },
] as const;

export type TeamSetupTab = (typeof TEAM_SETUP_TABS)[number]['key'];

export function isTeamSetupTab(tab: string | undefined): tab is TeamSetupTab {
  return TEAM_SETUP_TABS.some((t) => t.key === tab);
}

/**
 * Who opens a person in the Evrella console: the owner and admin ranks, the
 * ones that carry `systemdox:contacts:read`. Anyone else reads the role and
 * the identifier as plain text.
 */
export function canOpenPeople(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Where an identifier opens, in a new tab of the Evrella console:
 * - a `con_` opens the organisation's Contacts page, which has no address for
 *   one contact, so it opens on its list;
 * - a `ctc_` or `usr_` opens Manage organisation, where Staff is.
 * `orgId` is the workspace's organisation (`org_`, ADR-127 Amendment 10).
 */
export function evrellaPersonUrl(orgId: string, personId: string): string | null {
  const base = `https://app.evrella.com/o/${encodeURIComponent(orgId)}`;
  if (personId.startsWith('con_')) return `${base}/contacts`;
  if (personId.startsWith('ctc_') || personId.startsWith('usr_')) return `${base}/organisation`;
  return null;
}

const KIND: Record<ValueAnswerListed['kind'], string> = {
  phone: 'Phone number',
  email: 'Email address',
};

const ANSWER: Record<ValueAnswerListed['answer'], string> = {
  ours: 'Ours',
  member: 'A member',
  person: 'A person',
};

export function kindText(kind: ValueAnswerListed['kind']): string {
  return KIND[kind] ?? kind;
}

export function answerText(answer: ValueAnswerListed['answer']): string {
  return ANSWER[answer] ?? answer;
}

/** `2026-10-31 17:05` from an ISO date-time, in UTC as the API serves it. */
export function when(iso: string | undefined): string {
  return iso ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}`.trim() : '';
}
